import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from './api';

export type CycleType = 'quarterly' | 'half_year' | 'annual' | 'adhoc';
export type CycleStatus = 'draft' | 'open' | 'review' | 'closed';
export type ReviewKind = 'self' | 'manager' | 'peer' | 'skip_level';
export type ReviewStatus = 'draft' | 'submitted' | 'acknowledged';
export type GoalStatus = 'active' | 'on_track' | 'at_risk' | 'done' | 'dropped';

export interface ReviewCycle {
  id: string;
  name: string;
  cycleType: CycleType;
  startsOn: string;
  endsOn: string;
  status: CycleStatus;
  createdAt: string;
}

export interface PerformanceReview {
  id: string;
  cycleId: string;
  subjectEmployeeId: string;
  reviewerUserId: string | null;
  reviewerEmployeeId: string | null;
  kind: ReviewKind;
  rating: number | null;
  strengths: string | null;
  growthAreas: string | null;
  comments: string | null;
  status: ReviewStatus;
  submittedAt: string | null;
  createdAt: string;
}

export interface PerformanceGoal {
  id: string;
  employeeId: string;
  cycleId: string | null;
  title: string;
  description: string | null;
  metric: string | null;
  dueOn: string | null;
  progress: number;
  status: GoalStatus;
  createdAt: string;
  updatedAt: string;
}

export function useCycles() {
  return useQuery({
    queryKey: ['perf-cycles'],
    queryFn: async () => {
      const r = await api.get<{ cycles: ReviewCycle[] }>('/api/performance/cycles');
      return r.data.cycles;
    },
  });
}

export function useCreateCycle() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (args: { name: string; cycleType: CycleType; startsOn: string; endsOn: string }) => {
      const r = await api.post<ReviewCycle>('/api/performance/cycles', args);
      return r.data;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['perf-cycles'] }),
  });
}

export function useSetCycleStatus() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (args: { id: string; status: CycleStatus }) => {
      const r = await api.post<ReviewCycle>(`/api/performance/cycles/${args.id}/status`, {
        status: args.status,
      });
      return r.data;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['perf-cycles'] }),
  });
}

export function useCycleReviews(cycleId: string | undefined) {
  return useQuery({
    queryKey: ['perf-reviews', 'cycle', cycleId],
    enabled: !!cycleId,
    queryFn: async () => {
      const r = await api.get<{ reviews: PerformanceReview[] }>(`/api/performance/cycles/${cycleId}/reviews`);
      return r.data.reviews;
    },
  });
}

export function useEmployeeReviews(employeeId: string | undefined) {
  return useQuery({
    queryKey: ['perf-reviews', 'employee', employeeId],
    enabled: !!employeeId,
    queryFn: async () => {
      const r = await api.get<{ reviews: PerformanceReview[] }>(`/api/performance/employees/${employeeId}/reviews`);
      return r.data.reviews;
    },
  });
}

export function useCreateReview() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (args: { cycleId: string; subjectEmployeeId: string; kind: ReviewKind; reviewerEmployeeId?: string }) => {
      const { cycleId, ...body } = args;
      const r = await api.post<PerformanceReview>(`/api/performance/cycles/${cycleId}/reviews`, body);
      return r.data;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['perf-reviews'] }),
  });
}

export function useSubmitReview() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (args: { id: string; rating?: number; strengths?: string; growthAreas?: string; comments?: string }) => {
      const { id, ...body } = args;
      const r = await api.post<PerformanceReview>(`/api/performance/reviews/${id}/submit`, body);
      return r.data;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['perf-reviews'] }),
  });
}

export function useGoals(employeeId?: string) {
  return useQuery({
    queryKey: ['perf-goals', employeeId ?? 'all'],
    queryFn: async () => {
      const r = await api.get<{ goals: PerformanceGoal[] }>(
        `/api/performance/goals${employeeId ? `?employeeId=${employeeId}` : ''}`,
      );
      return r.data.goals;
    },
  });
}

export function useCreateGoal() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (args: {
      employeeId: string;
      cycleId?: string;
      title: string;
      description?: string;
      metric?: string;
      dueOn?: string;
    }) => {
      const r = await api.post<PerformanceGoal>('/api/performance/goals', args);
      return r.data;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['perf-goals'] }),
  });
}

export function useUpdateGoal() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (args: { id: string; progress?: number; status?: GoalStatus }) => {
      const r = await api.post<PerformanceGoal>(`/api/performance/goals/${args.id}`, {
        progress: args.progress, status: args.status,
      });
      return r.data;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['perf-goals'] }),
  });
}
